import React, { JSX, useEffect, useRef, useState } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';
import Icons from 'components/atoms/Icons';
import { FacetValue, FilterPanelType } from 'components/types/FilterTypes';
import { StyleguideComponentProps } from 'lib/component-props';
import {
  buildFilterOptions,
  emptyFacets,
  filterDefaults,
  filterPanelsDefaults,
  getResultsCountLabel,
  PAGE_SIZE,
  DEFAULT_SPONSOR,
  CLINICAL_TRIALS_STUDY_BASE,
  parseStudyIds,
  toCancerTypeFacets,
  uniqueConditionLabels,
  uniqueConditions,
} from 'lib/clinical-trials/api';
import {
  fetchCancerTypes,
  searchClinicalTrials,
} from 'lib/clinical-trials/client';
import {
  ClinicalTrial,
  ClinicalTrialsFilterOptionsType,
  ClinicalTrialsFiltersType,
  ClinicalTrialSort,
} from 'lib/clinical-trials/types';
import ClinicalTrialsDetails from './ClinicalTrialsDetails';
import ClinicalTrialsFilters from './ClinicalTrialsFilters';
import ClinicalTrialsSearchBar from './ClinicalTrialsSearchBar';
import ClinicalTrialsSearchResults from './ClinicalTrialsSearchResults';
import ClinicalTrialsSelectedFilters from './ClinicalTrialsSelectedFilters';

export type ClinicalTrialsSearchProps = StyleguideComponentProps & {
  fields?: {
    DefaultSponsor?: Field<string>;
    InterestUrl?: Field<string>;
    StudyId?: Field<string>;
    'Study Id'?: Field<string>;
  };
};

type ClinicalTrialsSearchState = {
  activeFilterPanel: string;
  apiError: boolean;
  query: string;
  queryInputValue: string;
  page: number;
  pageSize: number;
  results: ClinicalTrial[];
  count: number;
  sort: ClinicalTrialSort;
  filters: ClinicalTrialsFilterOptionsType;
  selectedFilters: ClinicalTrialsFiltersType;
  selectedFacets: Record<string, FacetValue[]>;
  filterPanels: FilterPanelType[];
  mobileControlsOpen: boolean;
  controlPanel: string;
  loading: boolean;
  pageTokens: Record<number, string | undefined>;
};

const buildUrlFromTemplate = (
  template: string | undefined,
  nctId: string,
  fallback: string
): string => {
  if (!template?.trim()) return fallback;
  return template.replace(/\{nctId\}/gi, nctId);
};

const ClinicalTrialsSearch = (
  props: ClinicalTrialsSearchProps
): JSX.Element => {
  const { fields, rendering } = props;
  const defaultSponsor =
    fields?.DefaultSponsor?.value?.trim() || DEFAULT_SPONSOR;
  const studyIds = parseStudyIds(
    fields?.StudyId?.value || fields?.['Study Id']?.value
  );
  const [selectedNctId, setSelectedNctId] = useState('');

  const getInterestUrl = (nctId: string) =>
    buildUrlFromTemplate(
      fields?.InterestUrl?.value,
      nctId,
      `${CLINICAL_TRIALS_STUDY_BASE}/${nctId}#contacts-locations`
    );

  const [state, setState] = useState<ClinicalTrialsSearchState>({
    activeFilterPanel: '',
    apiError: false,
    query: filterDefaults.query,
    queryInputValue: filterDefaults.query,
    page: filterDefaults.page,
    pageSize: PAGE_SIZE,
    results: [],
    count: 0,
    sort: filterDefaults.sort,
    filters: buildFilterOptions(),
    selectedFilters: {
      acceptingnewpatients: [...filterDefaults.acceptingnewpatients],
      cancertypes: [...filterDefaults.cancertypes],
      phase: [...filterDefaults.phase],
    },
    selectedFacets: emptyFacets(),
    filterPanels: filterPanelsDefaults.map(panel => ({ ...panel })),
    mobileControlsOpen: false,
    controlPanel: '',
    loading: true,
    pageTokens: { 1: undefined },
  });

  const searchRequestId = useRef(0);
  const pageTokensRef = useRef(state.pageTokens);
  pageTokensRef.current = state.pageTokens;

  const selectedFacetsCount = Object.values(state.selectedFacets).reduce(
    (sum, facets) => sum + (facets?.length || 0),
    0
  );

  const syncBodyClass = (open: boolean) => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('mobile-controls-open', open);
  };

  const closeMobileControls = () => {
    syncBodyClass(false);
    setState(prev => ({
      ...prev,
      mobileControlsOpen: false,
      controlPanel: '',
    }));
  };

  const openDetails = (nctId: string) => {
    closeMobileControls();
    setSelectedNctId(nctId);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const closeDetails = () => {
    setSelectedNctId('');
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const openMobileControls = (control: string) => {
    syncBodyClass(true);
    setState(prev => ({
      ...prev,
      mobileControlsOpen: true,
      controlPanel: control,
    }));
  };

  const updateControlPanel = (control: string) => {
    if (state.mobileControlsOpen) {
      closeMobileControls();
    } else {
      openMobileControls(control);
    }
  };

  const setActiveFilterPanel = (id: string) => {
    setState(prev => ({
      ...prev,
      activeFilterPanel: id,
      filterPanels: prev.filterPanels.map(panel => ({
        ...panel,
        active: panel.id === id && prev.activeFilterPanel !== id,
      })),
    }));
  };

  const onQueryInputChange = (value: string) => {
    setState(prev => ({ ...prev, queryInputValue: value }));
  };

  const updateQuery = (value: string) => {
    setState(prev => ({
      ...prev,
      query: value,
      queryInputValue: value,
      page: 1,
      pageTokens: { 1: undefined },
    }));
  };

  const updatePage = (page: number) => {
    const nextPage = Number(page);
    if (!Number.isInteger(nextPage) || nextPage < 1) return;

    setState(prev =>
      prev.page === nextPage ? prev : { ...prev, page: nextPage }
    );

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  const addFilter = (filter: string, item: string, facet: FacetValue) => {
    setState(prev => {
      const current = prev.selectedFilters[
        filter as keyof ClinicalTrialsFiltersType
      ] as string[];
      if (current.includes(item)) return prev;

      return {
        ...prev,
        page: 1,
        pageTokens: { 1: undefined },
        selectedFilters: {
          ...prev.selectedFilters,
          [filter]: [...current, item],
        },
        selectedFacets: {
          ...prev.selectedFacets,
          [filter]: [...(prev.selectedFacets[filter] || []), facet],
        },
      };
    });
  };

  const removeFilter = (filter: string, item: string) => {
    setState(prev => {
      const currentFilters = prev.selectedFilters[
        filter as keyof ClinicalTrialsFiltersType
      ] as string[];
      return {
        ...prev,
        page: 1,
        pageTokens: { 1: undefined },
        selectedFilters: {
          ...prev.selectedFilters,
          [filter]: currentFilters.filter(value => value !== item),
        },
        selectedFacets: {
          ...prev.selectedFacets,
          [filter]: (prev.selectedFacets[filter] || []).filter(
            facet => facet.text !== item
          ),
        },
      };
    });
  };

  const clearSelectedFilters = () => {
    setState(prev => ({
      ...prev,
      page: 1,
      pageTokens: { 1: undefined },
      selectedFilters: {
        acceptingnewpatients: [],
        cancertypes: [],
        phase: [],
      },
      selectedFacets: emptyFacets(),
    }));
  };

  const clearAllFilters = () => {
    setState(prev => ({
      ...prev,
      query: '',
      queryInputValue: '',
      page: 1,
      pageTokens: { 1: undefined },
      sort: filterDefaults.sort,
      selectedFilters: {
        acceptingnewpatients: [],
        cancertypes: [],
        phase: [],
      },
      selectedFacets: emptyFacets(),
      filterPanels: prev.filterPanels.map(panel => ({
        ...panel,
        active: false,
      })),
      activeFilterPanel: '',
    }));
  };

  useEffect(() => {
    const requestId = ++searchRequestId.current;
    let cancelled = false;

    const runSearch = async () => {
      setState(prev => ({ ...prev, loading: true, apiError: false }));

      try {
        const requestedPage = state.page;
        const pageToken =
          requestedPage === 1
            ? undefined
            : pageTokensRef.current[requestedPage];

        const acceptingNewPatients =
          state.selectedFilters.acceptingnewpatients.includes('true');
        const defaultScope = studyIds.length
          ? { studyIds }
          : { sponsor: defaultSponsor };

        const response = await searchClinicalTrials({
          sort: state.sort,
          pageSize: state.pageSize,
          ...defaultScope,
          ...(state.query.trim() ? { query: state.query } : {}),
          ...(state.selectedFilters.cancertypes.length
            ? { cancerTypes: state.selectedFilters.cancertypes }
            : {}),
          ...(state.selectedFilters.phase.length
            ? { phases: state.selectedFilters.phase }
            : {}),
          ...(acceptingNewPatients ? { acceptingNewPatients: true } : {}),
          ...(pageToken ? { pageToken } : {}),
        });

        if (cancelled || requestId !== searchRequestId.current) return;

        if (response.nextPageToken) {
          pageTokensRef.current = {
            ...pageTokensRef.current,
            [requestedPage + 1]: response.nextPageToken,
          };
        }

        setState(prev => ({
          ...prev,
          loading: false,
          results: response.studies,
          count:
            response.totalCount > 0
              ? response.totalCount
              : requestedPage > 1 && prev.count > 0
                ? prev.count
                : response.totalCount,
          pageTokens: pageTokensRef.current,
          filters: {
            ...prev.filters,
            cancertypes: toCancerTypeFacets(
              uniqueConditionLabels([
                ...prev.filters.cancertypes.map(facet => facet.text),
                ...uniqueConditions(response.studies),
              ])
            ),
          },
        }));
      } catch (error) {
        if (cancelled || requestId !== searchRequestId.current) return;
        console.error('Clinical trials search failed', error);
        setState(prev => ({
          ...prev,
          loading: false,
          apiError: true,
          results: [],
          count: 0,
        }));
      }
    };

    runSearch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.query,
    state.selectedFilters.acceptingnewpatients.join(','),
    state.selectedFilters.cancertypes.join(','),
    state.selectedFilters.phase.join(','),
    state.sort,
    state.page,
    state.pageSize,
    defaultSponsor,
    studyIds.join(','),
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadCancerTypes = async () => {
      try {
        const cancerTypes = await fetchCancerTypes(
          studyIds.length ? { studyIds } : { sponsor: defaultSponsor }
        );
        if (cancelled) return;

        setState(prev => {
          const allowed = new Set(cancerTypes.map(type => type.toLowerCase()));
          const nextSelected = prev.selectedFilters.cancertypes.filter(type =>
            allowed.has(type.toLowerCase())
          );
          const selectedUnchanged =
            nextSelected.length === prev.selectedFilters.cancertypes.length &&
            nextSelected.every(
              (type, index) => type === prev.selectedFilters.cancertypes[index]
            );

          return {
            ...prev,
            filters: {
              ...prev.filters,
              cancertypes: toCancerTypeFacets(cancerTypes),
            },
            ...(selectedUnchanged
              ? {}
              : {
                  selectedFilters: {
                    ...prev.selectedFilters,
                    cancertypes: nextSelected,
                  },
                  selectedFacets: {
                    ...prev.selectedFacets,
                    cancertypes: (prev.selectedFacets.cancertypes || []).filter(
                      facet => allowed.has(facet.text.toLowerCase())
                    ),
                  },
                }),
          };
        });
      } catch (error) {
        console.error('Clinical trials cancer types failed', error);
      }
    };

    loadCancerTypes();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSponsor, studyIds.join(',')]);

  useEffect(() => {
    return () => {
      syncBodyClass(false);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const size = getOsfViewportSize();
      if (size === 'lg' || size === 'xl') {
        closeMobileControls();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className="search-page overhauled clinical-trials-search"
      id={`clinical-trials-search-${rendering?.uid}`}
    >
      {selectedNctId ? (
        <div className="jss-inner-container">
          <ClinicalTrialsDetails
            nctId={selectedNctId}
            onBack={closeDetails}
            interestUrlTemplate={fields?.InterestUrl?.value}
          />
        </div>
      ) : (
        <>
          <ClinicalTrialsSearchBar
            queryInputValue={state.queryInputValue}
            onQueryInputChange={onQueryInputChange}
            updateQuery={updateQuery}
          />

          <div className="jss-inner-container">
            <div className="search-page-controls only-mobile">
              <h2 className="screen-recording-only" tabIndex={0}>
                Filters
              </h2>
              <div className="control-toggles">
                <div className="mob-filter-wrapper filter-sidebar">
                  <div className="mob-filter">
                    <button
                      type="button"
                      className="mob-filter-button"
                      aria-label="Open filters"
                      onClick={() => updateControlPanel('filters')}
                    >
                      <span className="mob-filter-button__label">
                        <Icons id="icon-filters" />
                        {selectedFacetsCount > 0
                          ? `Filters (${selectedFacetsCount})`
                          : 'Filters'}
                      </span>
                      <Icons id="pld-chevron-right" className="chevron-right" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <ClinicalTrialsSelectedFilters
              selectedFilters={state.selectedFilters}
              removeFilter={removeFilter}
              clearAllFilters={clearSelectedFilters}
            />

            <div className="search-page-wrapper clinical-trials">
              <div className="clinical-trials-results-header">
                <p className="clinical-trials-results-count">
                  {state.loading
                    ? 'Loading...'
                    : getResultsCountLabel(
                        state.count,
                        state.page,
                        state.pageSize
                      )}
                </p>
              </div>

              <div className="left-col">
                <div
                  className="bg-overlay"
                  onClick={e => {
                    if (state.mobileControlsOpen) {
                      const targ = e.target as HTMLElement;
                      if (
                        !targ.classList.contains('filter-bar-wrapper') &&
                        !targ.closest('.filter-bar-wrapper')
                      ) {
                        updateControlPanel('filters');
                      }
                    }
                  }}
                >
                  <ClinicalTrialsFilters
                    clearAllFilters={clearAllFilters}
                    filterPanels={state.filterPanels}
                    setActiveFilterPanel={setActiveFilterPanel}
                    filters={state.filters}
                    addFilter={addFilter}
                    removeFilter={removeFilter}
                    selectedFacets={state.selectedFacets}
                    selectedFacetsCount={selectedFacetsCount}
                    selectedFilters={state.selectedFilters}
                    activeFilterPanel={state.activeFilterPanel}
                    closeMobileControls={closeMobileControls}
                    resultsCount={state.count}
                  />
                </div>

                <div className="search-page-results">
                  <ClinicalTrialsSearchResults
                    apiError={state.apiError}
                    results={state.results}
                    page={state.page}
                    pageSize={state.pageSize}
                    clearAllFilters={clearAllFilters}
                    count={state.count}
                    updatePage={updatePage}
                    loading={state.loading}
                    getInterestUrl={getInterestUrl}
                    onLearnMore={openDetails}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClinicalTrialsSearch;
